import { useTitle } from "@/hooks/use-title.hook.ts";
import { useUnsavedChangesBlocker } from "@/hooks/use-unsaved-changes-blocker.hook.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { JobRoleEditModal } from "@/pages/settings/jobs/roles/widgets/JobRoleEditModal.tsx";
import { useJobLevels } from "@/queries/use-job-levels.query.ts";
import { useJobProfiles, useJobProfileMatrix } from "@/queries/use-job-profiles.query.ts";
import { useJobRoleById, useJobRoleDelete } from "@/queries/use-job-roles.query.ts";
import { useJobGrades, useJobTracks } from "@/queries/use-organization-lists.query.ts";
import { ApiJobLevel } from "@/types/job-level.types.ts";
import { ApiOrganizationListItem } from "@/types/organization-list.types.ts";
import {
  Box,
  Button,
  Checkbox,
  ConfirmationModal,
  DotSeparator,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Page,
  PageHeader,
  Stack,
  Table,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { JobProfileEditModal } from "@/pages/settings/jobs/roles/widgets/JobProfileEditModal.tsx";

interface FrameworkRow {
  grade: ApiOrganizationListItem;
  levelsByTrack: Map<number, ApiJobLevel>;
}

const mergeAndSortById = <T extends { id: number; sort: number }>(active: T[], referenced: T[]) => {
  const itemsById = new Map(active.map((item) => [item.id, item]));
  referenced.forEach((item) => itemsById.set(item.id, item));

  return Array.from(itemsById.values()).sort(
    (left, right) => left.sort - right.sort || left.id - right.id
  );
};

export interface JobRolePageProps {
  mode?: "index" | "edit";
}

export function JobRolePage({ mode = "index" }: JobRolePageProps) {
  const { job_role_id } = useParams<{ job_role_id: string }>();
  const { t } = useTranslation();
  const { openModal } = useModals();
  const navigate = useNavigate();

  const isEditMode = mode === "edit";
  const roleId = parseInt(job_role_id ?? "0", 10);

  const { data: role, isLoading: isRoleLoading } = useJobRoleById(roleId);
  const { data: gradesData, isLoading: isGradesLoading } = useJobGrades();
  const { data: tracksData, isLoading: isTracksLoading } = useJobTracks();
  const { data: levelsData, isLoading: isLevelsLoading } = useJobLevels();
  const { data: profilesData } = useJobProfiles();

  const profileMatrix = useJobProfileMatrix();
  const roleDelete = useJobRoleDelete();

  useTitle(role?.title ?? "");

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.jobCatalog.roleViewed);
  });

  const roleProfiles = useMemo(() => {
    return profilesData?.filter((p) => p.role.id === roleId) ?? [];
  }, [profilesData, roleId]);

  const grades = useMemo(
    () =>
      mergeAndSortById(
        gradesData?.items.filter((grade) => !grade.is_archived) ?? [],
        roleProfiles.map((profile) => profile.level.grade)
      ),
    [gradesData, roleProfiles]
  );
  const tracks = useMemo(
    () =>
      mergeAndSortById(
        tracksData?.items.filter((track) => !track.is_archived) ?? [],
        roleProfiles.map((profile) => profile.level.track)
      ),
    [tracksData, roleProfiles]
  );
  const levels = useMemo(
    () =>
      mergeAndSortById(
        levelsData?.filter((level) => !level.is_archived) ?? [],
        roleProfiles.map((profile) => profile.level)
      ),
    [levelsData, roleProfiles]
  );

  const initialLevelIds = useMemo(
    () => new Set(roleProfiles.map((profile) => profile.level.id)),
    [roleProfiles]
  );
  const selectionSourceKey = `${mode}:${Array.from(initialLevelIds)
    .sort((a, b) => a - b)
    .join(",")}`;
  const [selectionState, setSelectionState] = useState(() => ({
    sourceKey: selectionSourceKey,
    levelIds: isEditMode ? initialLevelIds : new Set<number>(),
  }));

  if (selectionState.sourceKey !== selectionSourceKey) {
    setSelectionState({
      sourceKey: selectionSourceKey,
      levelIds: isEditMode ? initialLevelIds : new Set<number>(),
    });
  }

  const selectedLevelIds = selectionState.levelIds;
  const setSelectedLevelIds = (update: (current: Set<number>) => Set<number>) => {
    setSelectionState((current) => ({
      ...current,
      levelIds: update(current.levelIds),
    }));
  };

  const hasUnsavedChanges =
    isEditMode &&
    (selectedLevelIds.size !== initialLevelIds.size ||
      Array.from(selectedLevelIds).some((levelId) => !initialLevelIds.has(levelId)));
  const { navigateWithoutBlocking } = useUnsavedChangesBlocker(hasUnsavedChanges);

  const handleEditRole = () => {
    if (!role) return;
    openModal(JobRoleEditModal, { role });
  };

  const handleEditProfiles = () => {
    navigate(`/settings/jobs/roles/${roleId}/edit`);
  };

  const handleDeleteRole = () => {
    if (!role) return;

    openModal(ConfirmationModal, {
      question: t("core:delete_job_role_title"),
      text: t("core:delete_job_role_description"),
      acceptTitle: t("common:delete"),
      onAccept: async () => {
        await roleDelete.mutateAsync(role.id);
        navigate("/settings/jobs/framework", { replace: true });
      },
    });
  };

  const handleCancel = () => {
    navigate(`/settings/jobs/roles/${roleId}`);
  };

  const handleSave = async () => {
    await profileMatrix.mutateAsync({
      job_role_id: roleId,
      level_ids: Array.from(selectedLevelIds),
    });
    analyticsService.trackEvent(analyticEvents.jobCatalog.profileEdited);
    navigateWithoutBlocking(() => navigate(`/settings/jobs/roles/${roleId}`));
  };

  const handleToggleLevel = (level: ApiJobLevel) => {
    const isSelected = selectedLevelIds.has(level.id);
    const hasArchivedDimension =
      level.is_archived || level.track.is_archived || level.grade.is_archived;

    if (!isSelected && hasArchivedDimension) {
      return;
    }

    setSelectedLevelIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(level.id)) {
        newSet.delete(level.id);
      } else {
        newSet.add(level.id);
      }

      return newSet;
    });
  };

  const tableData = useMemo<FrameworkRow[]>(() => {
    return grades.map((grade) => {
      const levelsByTrack = new Map<number, ApiJobLevel>();
      levels.forEach((level) => {
        if (level.grade.id === grade.id) {
          levelsByTrack.set(level.track.id, level);
        }
      });
      return { grade, levelsByTrack };
    });
  }, [grades, levels]);

  const columns = useMemo<ColumnDef<FrameworkRow>[]>(() => {
    const cols: ColumnDef<FrameworkRow>[] = [
      {
        id: "grade",
        header: t("core:grades"),
        accessorFn: (row) => row.grade.title,
        size: 150,
        cell: ({ row }) => {
          const grade = row.original.grade;
          const title = <Text className="font-semibold text-default">{grade.title}</Text>;

          if (!grade.is_archived) {
            return title;
          }

          return (
            <Stack direction="horizontal" items="center" gap="xs">
              <Tooltip>
                <TooltipTrigger>
                  <Stack direction="horizontal" items="center" gap="xs">
                    {title}
                    <FontAwesomeIcon className="text-secondary" icon="fa-light fa-archive" />
                  </Stack>
                </TooltipTrigger>
                <TooltipContent>{t("core:job_grade_archived")}</TooltipContent>
              </Tooltip>
            </Stack>
          );
        },
      },
    ];

    tracks.forEach((track) => {
      cols.push({
        id: `track-${track.id}`,
        header: () =>
          track.is_archived ? (
            <Tooltip>
              <TooltipTrigger>
                <Stack direction="horizontal" items="center" gap="xs">
                  <Text>{track.title}</Text>
                  <FontAwesomeIcon className="text-secondary" icon="fa-light fa-archive" />
                </Stack>
              </TooltipTrigger>
              <TooltipContent>{t("core:job_track_archived")}</TooltipContent>
            </Tooltip>
          ) : (
            track.title
          ),
        size: 160,
        cell: ({ row }) => {
          const level = row.original.levelsByTrack.get(track.id);

          if (!level) {
            return <Text className="text-secondary">-</Text>;
          }

          if (isEditMode) {
            const isSelected = selectedLevelIds.has(level.id);
            const hasArchivedDimension =
              level.is_archived || level.track.is_archived || level.grade.is_archived;
            const isArchivedLevelDisabled = hasArchivedDimension && !isSelected;

            const levelContent = (
              <Box className={isArchivedLevelDisabled ? "opacity-60" : ""}>
                <Stack direction="horizontal" items="center">
                  <Checkbox
                    checked={isSelected}
                    disabled={isArchivedLevelDisabled}
                    onChange={() => handleToggleLevel(level)}
                  />
                  <Text>{level.title}</Text>
                </Stack>
              </Box>
            );

            if (!isArchivedLevelDisabled) {
              return levelContent;
            }

            const archivedTooltip =
              level.track.is_archived && level.grade.is_archived
                ? t("core:job_track_and_grade_archived")
                : level.track.is_archived
                  ? t("core:job_track_archived")
                  : level.grade.is_archived
                    ? t("core:job_grade_archived")
                    : t("core:job_level_archived");

            return (
              <Tooltip>
                <TooltipTrigger>{levelContent}</TooltipTrigger>
                <TooltipContent>{archivedTooltip}</TooltipContent>
              </Tooltip>
            );
          }

          const profile = roleProfiles.find((p) => p.level.id === level.id);

          if (!profile) {
            return <Text className="text-secondary">-</Text>;
          }

          return (
            <Text
              className="text-secondary hover:underline cursor-pointer"
              onClick={() => {
                openModal(JobProfileEditModal, {
                  profile,
                });
              }}
            >
              {profile.display_title ?? profile.system_title}
            </Text>
          );
        },
      });
    });

    return cols;
  }, [tracks, isEditMode, selectedLevelIds, roleProfiles]);

  const isLoading = isRoleLoading || isGradesLoading || isTracksLoading || isLevelsLoading;

  return (
    <Page className={"h-full overflow-auto relative flex-1 grow"}>
      <PageHeader
        titleSlot={
          <Box className={"text-lg font-semibold"}>
            <DotSeparator>
              <Text>{t(isEditMode ? "core:edit_job_profiles" : "core:job_profiles")}</Text>
              <Text>{role?.title ?? ""}</Text>
            </DotSeparator>
          </Box>
        }
      >
        <Stack direction={"horizontal"} gap={"sm"}>
          {isEditMode ? (
            <>
              <Button variant="secondary" onClick={handleCancel} disabled={profileMatrix.isPending}>
                {t("common:cancel")}
              </Button>
              <Button onClick={handleSave} isLoading={profileMatrix.isPending}>
                {t("common:save")}
              </Button>
            </>
          ) : (
            <DropdownMenu trigger={<Button>{t("common:edit")}</Button>}>
              <DropdownMenuItem title={t("core:job_role")} onClick={handleEditRole} />
              <DropdownMenuItem title={t("core:job_profiles")} onClick={handleEditProfiles} />
              <DropdownMenuItem
                title={`${t("core:delete_role")}...`}
                variant="menuDanger"
                onClick={handleDeleteRole}
              />
            </DropdownMenu>
          )}
        </Stack>
      </PageHeader>

      <Page.Content>
        {grades.length === 0 || tracks.length === 0 ? (
          <Stack className="p-6" gap="md">
            <Text className="text-secondary">{t("core:career_framework_empty_description")}</Text>
          </Stack>
        ) : (
          <Table data={tableData} columns={columns} showSkeleton={isLoading} />
        )}
      </Page.Content>
    </Page>
  );
}
